<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :title="title"
      ref="grid"
      :actions="actions"
      :beforeSetupGrid="beforeSetupGrid"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
    ></dx-grid>

    <v-dialog persistent v-model="updateDialog" max-width="950">
      <v-card class="pa-3">
        <!-- Title row -->
        <v-layout row align-center class="mb-2">
          <v-card-title class="headline pl-0 pt-0 pb-0">
            Voting Registration Form
            <span class="ml-2 text-info">({{ rowClicked.formType }})</span>
          </v-card-title>
          <v-spacer></v-spacer>
          <div v-if="rowClicked.submittedAt" class="text-xs-right mr-2">
            <div class="caption grey--text">Submitted</div>
            <div class="body-2 font-weight-medium">
              {{ rowClicked.submittedAt | date }}
            </div>
            <div class="caption grey--text">
              by {{ rowClicked.submittedBy }}
            </div>
          </div>
        </v-layout>

        <!-- DOE Mapping Banner -->
        <div
          v-if="rowClicked.status === 'SUBMITTED'"
          class="doe-mapping-banner mb-3 pa-2"
          :class="
            rowClicked.matchedToDoeID
              ? 'doe-mapping-banner--matched'
              : 'doe-mapping-banner--unmatched'
          "
        >
          <v-layout row align-center>
            <v-icon
              class="mr-2"
              :color="rowClicked.matchedToDoeID ? 'success' : 'grey darken-1'"
            >
              {{
                rowClicked.matchedToDoeID ? "mdi-check-circle" : "mdi-link-off"
              }}
            </v-icon>
            <div v-if="rowClicked.matchedToDoeID">
              <span class="font-weight-medium success--text"
                >Mapped to DOE</span
              >
              <span class="ml-3 body-2"
                >DOE ID: <strong>{{ rowClicked.matchedToDoeID }}</strong></span
              >
              <span class="ml-3 caption grey--text"
                >on {{ rowClicked.matchedToDoeOn | date }} by
                {{ rowClicked.matchedToDoeBy }}</span
              >
            </div>
            <div
              v-if="!rowClicked.matchedToDoeID"
              class="grey--text text--darken-1 body-2"
            >
              Not yet mapped to a DOE record
            </div>
            <v-spacer></v-spacer>
            <v-btn
              small
              @click="openDoeMatchDialog"
              :color="rowClicked.matchedToDoeID ? '' : 'success'"
              :class="rowClicked.matchedToDoeID ? 'doe-map-btn-mapped' : ''"
            >
              <v-icon
                left
                small
                :color="rowClicked.matchedToDoeID ? 'success' : ''"
                >mdi-link-variant</v-icon
              >
              {{
                rowClicked.matchedToDoeID ? "Manage DOE Match" : "Map to DOE"
              }}
            </v-btn>
          </v-layout>
        </div>

        <v-layout row>
          <v-layout column style="flex: 1;" class="mr-3">
            <div style="overflow-y: auto; height: 340px;">
              <v-layout
                row
                class="pa-1"
                v-for="prop in getProperties(rowClicked)"
              >
                <div class="font-weight-medium mr-2 width-200">
                  {{ prop.name }}:
                </div>
                <div>{{ prop.value }}</div>
              </v-layout>
            </div>
          </v-layout>
          <v-layout column style="min-width: 280px; max-width: 300px;">
            <div
              v-if="!rowClicked.pdfUrl"
              class="text-danger font-weight-medium mb-2"
            >
              Missing PDF Form
            </div>
            <v-btn class="mt-1" @click="downloadPdf" v-if="rowClicked.pdfUrl">
              <v-icon color="red" left dark>mdi-file-pdf</v-icon>
              PDF Form
              <v-icon right>mdi-download</v-icon>
            </v-btn>
            <v-btn class="mt-2" @click="downloadSignature">
              <v-icon color="red" left dark>mdi-image</v-icon>
              Signature
              <v-icon right>mdi-download</v-icon>
            </v-btn>
            <v-text-field
              v-model="confirmation"
              label="Confirmation"
              class="mt-3"
              outline
              hide-details
            ></v-text-field>
            <v-textarea
              v-model="note"
              label="Note"
              class="mt-2"
              rows="3"
              outline
              hide-details
            ></v-textarea>
            <v-select
              v-model="status"
              label="Status"
              class="mt-3"
              :items="['PENDING', 'SUBMITTED']"
              hide-details
            ></v-select>
          </v-layout>
        </v-layout>
        <v-card-actions class="mr-0 mt-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="updateDialog = false"
            >Cancel</v-btn
          >
          <v-btn :loading="processing" color="success" @click="save"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="votingDialog" max-width="1000">
      <voting-master-list-person
        :person="presonInfo"
        :reload="reload"
        :close="closeVotingDialog"
      ></voting-master-list-person>
    </v-dialog>

    <v-dialog persistent v-model="doeMatchDialog" max-width="1100">
      <v-card class="pa-4">
        <v-card-title class="headline pl-0 pt-0 pb-3"
          >Map to DOE Record</v-card-title
        >

        <!-- Form summary banner -->
        <div class="doe-form-banner mb-4 pa-3">
          <v-layout row justify-space-between align-center class="mb-2">
            <div class="caption font-weight-medium grey--text text--darken-1">
              REGISTRATION FORM
            </div>
            <v-chip
              v-if="rowClicked.matchedToDoeID"
              small
              color="success"
              text-color="white"
            >
              <v-icon left small color="white">mdi-check-circle</v-icon>
              Mapped: {{ rowClicked.matchedToDoeID }}
            </v-chip>
          </v-layout>
          <v-layout row wrap>
            <!-- Name block -->
            <div class="mr-5 mb-2">
              <div class="title font-weight-bold">
                {{ rowClicked.firstName
                }}<span v-if="rowClicked.middleInitial">
                  {{ rowClicked.middleInitial }}.</span
                >
                {{ rowClicked.lastName
                }}<span v-if="rowClicked.suffix"> {{ rowClicked.suffix }}</span>
              </div>
              <div class="caption grey--text" v-if="rowClicked.gender">
                {{ rowClicked.gender }}
              </div>
            </div>
            <!-- Address block -->
            <div class="mr-5 mb-2">
              <div class="caption grey--text">Address</div>
              <div class="body-2">
                {{ rowClicked.houseNumber }} {{ rowClicked.streetName
                }}<span v-if="rowClicked.apartment">
                  Apt {{ rowClicked.apartment }}</span
                >
              </div>
              <div class="body-2">
                {{ rowClicked.borough }} {{ rowClicked.zipCode }}
              </div>
            </div>
            <!-- DOB block -->
            <div class="mr-5 mb-2" v-if="rowClicked.dob">
              <div class="caption grey--text">Date of Birth</div>
              <div class="body-2">{{ rowClicked.dob | date }}</div>
            </div>
            <!-- Phone block -->
            <div
              class="mr-5 mb-2"
              v-if="rowClicked.homePhone || rowClicked.cellPhone"
            >
              <div class="caption grey--text">Phone</div>
              <div class="body-2" v-if="rowClicked.homePhone">
                {{ rowClicked.homePhone }}
              </div>
              <div class="body-2" v-if="rowClicked.cellPhone">
                {{ rowClicked.cellPhone }}
              </div>
            </div>
            <!-- Party block -->
            <div class="mr-5 mb-2" v-if="rowClicked.politicalParty">
              <div class="caption grey--text">Party</div>
              <div class="body-2">{{ rowClicked.politicalParty }}</div>
            </div>
            <!-- Account block -->
            <div class="mr-5 mb-2" v-if="rowClicked.accountID">
              <div class="caption grey--text">Account / Type</div>
              <div class="body-2">
                {{ rowClicked.accountID
                }}<span v-if="rowClicked.type"> · {{ rowClicked.type }}</span>
              </div>
            </div>
            <!-- Submitted block -->
            <div class="mb-2" v-if="rowClicked.submittedAt">
              <div class="caption grey--text">Submitted</div>
              <div class="body-2 font-weight-medium">
                {{ rowClicked.submittedAt | date }}
              </div>
              <div class="caption grey--text">
                by {{ rowClicked.submittedBy }}
              </div>
            </div>
          </v-layout>
        </div>

        <!-- Loading state -->
        <div v-if="doeLoading" class="text-xs-center py-5">
          <v-progress-circular
            indeterminate
            color="success"
            size="36"
          ></v-progress-circular>
          <div class="mt-3 body-1 grey--text">Searching DOE records...</div>
        </div>

        <!-- No results -->
        <div
          v-if="!doeLoading && !doeRecords.length"
          class="text-xs-center py-5 grey--text"
        >
          <v-icon large>mdi-account-off</v-icon>
          <div class="mt-2 body-1">
            {{
              rowClicked.matchedToDoeID
                ? "Mapped DOE record not found."
                : "No DOE match found."
            }}
          </div>
          <div class="caption mt-1" v-if="!rowClicked.matchedToDoeID">
            Checked: name prefix, normalized house/street, and date of birth.
          </div>
          <v-btn
            v-if="!rowClicked.matchedToDoeID"
            small
            outline
            color="primary"
            class="mt-3"
            @click="openManualDoeMatchDialog"
          >
            <v-icon left small>mdi-magnify</v-icon>
            Match Manually
          </v-btn>
        </div>

        <!-- DOE match rows -->
        <div
          v-if="!doeLoading && doeRecords.length"
          style="overflow-y: auto; max-height: 400px;"
        >
          <div class="caption grey--text mb-2">
            {{
              rowClicked.matchedToDoeID
                ? "Currently mapped DOE record"
                : doeRecords.length +
                  " match" +
                  (doeRecords.length !== 1 ? "es" : "") +
                  " found"
            }}
          </div>
          <div
            v-for="doe in doeRecords"
            :key="doe.id"
            @click="onDoeRowClick(doe)"
            class="doe-candidate-row pa-3 mb-2"
            :class="
              selectedDoeRecord && selectedDoeRecord.id === doe.id
                ? 'doe-candidate-selected'
                : ''
            "
          >
            <v-layout row align-center>
              <!-- Name + DOE ID + MI -->
              <div style="min-width: 210px;" class="mr-4">
                <div class="font-weight-bold body-1">
                  {{ doe.first
                  }}<span v-if="doe.mi && doe.mi.trim()">
                    {{ doe.mi.trim() }}.</span
                  >
                  {{ doe.last }}
                </div>
                <div class="caption grey--text">DOE ID: {{ doe.regID }}</div>
                <div class="caption grey--text" v-if="doe.s">
                  {{
                    doe.s === "M" ? "Male" : doe.s === "F" ? "Female" : doe.s
                  }}
                </div>
              </div>

              <!-- Address -->
              <div class="mr-4" style="min-width: 190px;">
                <div class="body-2">
                  {{ doe.house }} {{ doe.street
                  }}<span v-if="doe.apt && doe.apt.trim()">
                    Apt {{ doe.apt.trim() }}</span
                  >
                </div>
                <div class="caption grey--text">
                  {{ doe.city }} {{ doe.zip }}
                </div>
              </div>

              <!-- DOB -->
              <div class="mr-4" style="min-width: 110px;">
                <div class="caption grey--text">DOB</div>
                <div
                  v-if="doe.dob"
                  class="body-2"
                  :class="
                    formDob && doe.dob.slice(0, 10) !== formDob
                      ? 'doe-dob-mismatch'
                      : ''
                  "
                >
                  {{ doe.dob | date }}
                  <v-icon
                    v-if="formDob && doe.dob.slice(0, 10) !== formDob"
                    size="14"
                    color="warning"
                    style="vertical-align: middle;"
                    title="DOB does not match the registration form"
                    >mdi-alert</v-icon
                  >
                </div>
                <div
                  v-if="formDob && doe.dob && doe.dob.slice(0, 10) !== formDob"
                  class="caption doe-dob-mismatch-note"
                >
                  Form: {{ formDob | date }}
                </div>
              </div>

              <!-- Party -->
              <div class="mr-4" style="min-width: 60px;" v-if="doe.party">
                <div class="caption grey--text">Party</div>
                <div class="body-2">{{ doe.party.trim() }}</div>
              </div>

              <!-- Districts -->
              <div class="mr-4">
                <div class="caption grey--text">Districts</div>
                <div class="caption" v-if="doe.assembly">
                  Assembly: {{ doe.assembly }}
                </div>
                <div class="caption" v-if="doe.senate">
                  Senate: {{ doe.senate }}
                </div>
                <div class="caption" v-if="doe.council">
                  Council: {{ doe.council }}
                </div>
              </div>

              <!-- Reg date -->
              <div class="mr-4" v-if="doe.datE_REG">
                <div class="caption grey--text">DOE Registered</div>
                <div class="caption">{{ parseDoeRegDate(doe.datE_REG) }}</div>
              </div>

              <!-- Selected indicator -->
              <v-spacer></v-spacer>
              <v-icon
                v-if="selectedDoeRecord && selectedDoeRecord.id === doe.id"
                color="success"
                >mdi-check-circle</v-icon
              >
            </v-layout>
          </div>
        </div>

        <v-divider
          class="my-3"
          style="margin-left: 4px !important; margin-right: 4px !important;"
        ></v-divider>

        <v-card-actions class="pa-0 doe-dialog-actions">
          <v-btn
            v-if="rowClicked.matchedToDoeID"
            color="error"
            outline
            class="ml-0"
            :loading="doeMatchProcessing"
            @click="removeDoeMatch"
          >
            <v-icon left small>mdi-link-off</v-icon>
            Remove Match
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="default" dark class="mr-0" @click="closeDoeMatchDialog"
            >Cancel</v-btn
          >
          <v-btn
            v-if="!rowClicked.matchedToDoeID"
            color="success"
            :loading="doeMatchProcessing"
            :disabled="!selectedDoeRecord"
            @click="confirmDoeMatch"
          >
            <v-icon left small>mdi-check</v-icon>
            Confirm Match
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Manual DOE lookup dialog -->
    <v-dialog persistent v-model="manualDoeMatchDialog" max-width="1700">
      <v-card class="pa-3">
        <v-card-title class="headline pl-2 py-0"
          v-bind:class="{
            'mb-2': manualClickedDoe && manualClickedDoe.first,
            'mb-4': !manualClickedDoe || !manualClickedDoe.first,
          }"
        >
          Match Manually
        </v-card-title>

        <!-- Selected record preview -->
        <div
          style="border: #4caf50 2px solid;"
          class="mb-5"
          v-if="manualClickedDoe && manualClickedDoe.first"
        >
          <div
            class="px-2"
            style="background-color: #4caf50; color: #fff; width: fit-content;"
          >
            SELECTED:
          </div>
          <div class="pt-2 px-2 pb-1">
            <v-layout class="mb-2 pl-2">
              <div class="width-200 font-weight-medium">Name</div>
              <div>
                <span class="font-weight-regular mr-2">
                  ({{ manualClickedDoe.s == "M" ? "MALE" : "FEMALE" }})
                </span>
                {{ manualClickedDoe.first + " " + manualClickedDoe.last }}
              </div>
            </v-layout>
            <v-layout class="mb-2 pl-2">
              <div class="width-200 font-weight-medium">Address</div>
              <div>
                {{
                  manualClickedDoe.house +
                    " " +
                    manualClickedDoe.street +
                    (manualClickedDoe.apt && manualClickedDoe.apt.trim()
                      ? " Apt " + manualClickedDoe.apt.trim()
                      : "")
                }}
              </div>
            </v-layout>
            <v-layout class="mb-2 pl-2">
              <div class="width-200 font-weight-medium">DOE ID</div>
              <div>{{ manualClickedDoe.reG_ID }}</div>
            </v-layout>
            <v-layout class="mb-2 pl-2">
              <v-select
                v-model="manualClickedDoe.matchType"
                class="my-0 pt-2"
                style="max-width: 150px;"
                label="Match Type"
                :items="['Husband', 'Wife', 'Daughter', 'Son']"
                hide-details
              ></v-select>
            </v-layout>
          </div>
        </div>

        <div v-if="manualShowDoeGrid">
          <data-grid
            class="simple-datagrid pr-0 doe-table"
            :url="manualDoeUrl"
            :dontSaveFilters="true"
            :hideHeader="true"
            :perPage="6"
            :columns="manualDoeColumns"
            title="DOE"
            ref="manualDoeGrid"
            :beforeSetupGrid="beforeSetupManualDoeGrid"
            navigationPath="onManualDoeRowClicked"
            navigationField="onManualDoeRowClicked"
            v-bind:hideActions="true"
            :onRowClicked="onManualDoeRowClicked"
            :sortOptions="{
              enabled: true,
              field: 'last',
              type: 'asc',
            }"
          ></data-grid>
        </div>

        <v-card-actions class="mr-0 pb-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closeManualDoeMatchDialog">Cancel</v-btn>
          <v-btn
            color="success"
            :disabled="!manualClickedDoe || !manualClickedDoe.first"
            @click="selectManualMatch"
          >
            <v-icon left small>mdi-check</v-icon>
            Select Match
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="autoMatchDialog" max-width="1260">
      <v-card style="overflow: hidden;">
        <!-- Colored header bar -->
        <div class="automatch-header px-4 py-3">
          <v-layout row align-center>
            <div>
              <v-layout row align-center>
                <v-icon color="white" class="mr-2">mdi-link-variant</v-icon>
                <span class="title font-weight-medium white--text"
                  >Auto Match to DOE</span
                >
              </v-layout>
              <div class="caption mt-1" style="color: rgba(255,255,255,0.75);">
                Exact match on first name, last name, date of birth, house
                number &amp; street
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-chip
              v-if="!autoMatchLoading && autoMatchResults.length"
              color="white"
              style="color: #2e7d32;"
              class="font-weight-bold"
            >
              {{ autoMatchResults.length }} match{{
                autoMatchResults.length !== 1 ? "es" : ""
              }}
            </v-chip>
          </v-layout>
        </div>

        <!-- Loading -->
        <div v-if="autoMatchLoading" class="text-xs-center py-5 my-2">
          <v-progress-circular
            indeterminate
            color="success"
            size="44"
          ></v-progress-circular>
          <div class="mt-3 body-1 grey--text">Scanning for DOE matches...</div>
        </div>

        <!-- No results -->
        <div
          v-if="!autoMatchLoading && !autoMatchResults.length"
          class="text-xs-center py-5 my-2 grey--text"
        >
          <v-icon size="52" color="grey lighten-2">mdi-link-variant-off</v-icon>
          <div class="mt-3 body-1 grey--text text--darken-1">
            No automatic DOE matches found
          </div>
          <div class="caption mt-1">
            All submitted forms are either already matched or have no exact DOE
            record.
          </div>
        </div>

        <!-- Match list -->
        <div v-if="!autoMatchLoading && autoMatchResults.length">
          <div class="caption grey--text px-5 pt-3 pb-2">
            Review matches below. Remove any that are incorrect before
            confirming.
          </div>

          <!-- Column headers -->
          <v-layout row class="automatch-col-headers px-5 py-2">
            <div
              class="caption font-weight-bold text-uppercase letter-spacing"
              style="flex:1;"
            >
              Registration Form
            </div>
            <div style="width: 48px;"></div>
            <div
              class="caption font-weight-bold text-uppercase letter-spacing"
              style="flex:1;"
            >
              DOE Record
            </div>
            <div style="width: 48px;"></div>
          </v-layout>

          <div style="overflow-y: auto; max-height: 460px;" class="px-4 pb-3">
            <div
              v-for="(item, index) in autoMatchResults"
              :key="index"
              class="automatch-row mt-2"
            >
              <v-layout row align-center>
                <!-- Form side -->
                <div
                  class="automatch-side automatch-side--form pa-3"
                  style="flex:1;"
                >
                  <div class="body-2 font-weight-bold mb-1">
                    {{ item.form.firstName && item.form.firstName.trim() }}
                    {{ item.form.lastName }}
                  </div>
                  <div class="caption grey--text text--darken-1 mb-1">
                    {{ item.form.houseNumber && item.form.houseNumber.trim() }}
                    {{ item.form.streetName
                    }}<span
                      v-if="item.form.apartment && item.form.apartment.trim()"
                      >, Apt {{ item.form.apartment.trim() }}</span
                    >
                  </div>
                  <v-layout row wrap>
                    <span class="automatch-tag" v-if="item.form.dob"
                      >DOB: {{ item.form.dob | date }}</span
                    >
                    <span class="automatch-tag" v-if="item.form.accountID">{{
                      item.form.accountID
                    }}</span>
                    <span class="automatch-tag" v-if="item.form.type">{{
                      item.form.type
                    }}</span>
                  </v-layout>
                </div>

                  <!-- Arrow -->
                <div class="text-xs-center px-2" style="width:48px;">
                  <v-icon color="success" size="22"
                    >mdi-arrow-right-bold</v-icon
                  >
                </div>

                <!-- DOE side -->
                <div
                  class="automatch-side automatch-side--doe pa-3"
                  style="flex:1;"
                >
                  <div class="body-2 font-weight-bold mb-1">
                    {{ item.doe.first }} {{ item.doe.last }}
                  </div>
                  <div class="caption grey--text text--darken-1 mb-1">
                    {{ item.doe.house }} {{ item.doe.street
                    }}<span v-if="item.doe.apt && item.doe.apt.trim()"
                      >, Apt {{ item.doe.apt.trim() }}</span
                    >
                  </div>
                  <v-layout row wrap align-center>
                    <span
                      v-if="item.doe.dob"
                      :class="[
                        'automatch-tag',
                        item.isDobMatch === false ? 'automatch-tag--dob-mismatch' : '',
                      ]"
                    >
                      <v-icon
                        v-if="item.isDobMatch === false"
                        size="12"
                        color="warning"
                        style="vertical-align: middle; margin-right: 2px;"
                        >mdi-alert</v-icon
                      >DOE DOB: {{ item.doe.dob | date }}
                    </span>
                    <span
                      v-if="item.isDobMatch === false && item.form.dob"
                      class="automatch-tag automatch-tag--dob-mismatch"
                    >
                      Form DOB: {{ item.form.dob | date }}
                    </span>
                    <span
                      class="automatch-tag automatch-tag--id"
                      v-if="item.doe.reG_ID"
                      >ID: {{ item.doe.reG_ID }}</span
                    >
                    <span class="automatch-tag" v-if="item.doe.party"
                      >Party: {{ item.doe.party.trim() }}</span
                    >
                  </v-layout>
                </div>

                <!-- Remove -->
                <div class="px-2" style="width:48px; flex-shrink:0;">
                  <v-btn
                    icon
                    small
                    @click="removeAutoMatchItem(index)"
                    title="Remove this match"
                  >
                    <v-icon size="18" color="grey darken-1"
                      >mdi-close-circle-outline</v-icon
                    >
                  </v-btn>
                </div>
              </v-layout>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="automatch-footer px-5 py-3">
          <v-layout row align-center>
            <div v-if="autoMatchResults.length" class="caption grey--text">
              <v-icon small color="grey" class="mr-1"
                >mdi-information-outline</v-icon
              >
              {{ autoMatchResults.length }} match{{
                autoMatchResults.length !== 1 ? "es" : ""
              }}
              will be saved
            </div>
            <v-spacer></v-spacer>
            <v-btn
              flat
              :disabled="autoMatchProcessing"
              @click="closeAutoMatchDialog"
              class="mr-2"
              >Cancel</v-btn
            >
            <v-btn
              color="success"
              :loading="autoMatchProcessing"
              :disabled="!autoMatchResults.length"
              @click="saveAutoMatches"
            >
              <v-icon left small>mdi-check-all</v-icon>
              Confirm Matches
            </v-btn>
          </v-layout>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { formatCurrency, except } from "../../utils/helpers.js";
import votingMasterListPerson from "./VotingMasterListPerson.vue";

export default {
  components: {
    votingMasterListPerson,
  },
  data: () => ({
    title: "Online Voting Registration Forms",

    actions: [],
    userBranch: "",
    url: "",
    includeSubmitted: false,
    onlyUnmatched: false,
    regID: "",
    processing: false,

    selected: [],
    updateDialog: false,
    rowClicked: {},
    status: "",
    originalStatus: "",
    note: "",
    confirmation: "",

    votingDialog: false,
    presonInfo: {},

    doeMatchDialog: false,
    doeLoading: false,
    doeRecords: [],
    selectedDoeRecord: null,
    doeMatchProcessing: false,
    formDob: "",

    autoMatchDialog: false,
    autoMatchLoading: false,
    autoMatchProcessing: false,
    autoMatchResults: [],
    autoMatchIncludeAlreadyMatched: false,

    manualDoeMatchDialog: false,
    manualShowDoeGrid: false,
    manualClickedDoe: {},
    manualDoeUrl: "doe",
    manualDoeColumns: [
      {
        field: "isMatched",
        isIcon: true,
        icon: (value) => {
          return value ? "mdi-check-circle" : "mdi-alert";
        },
        iconColor: (value) => {
          return value ? "success" : "#f35123";
        },
      },
      {
        label: "ID",
        field: "reG_ID",
        sortable: true,
        hidden: false,
        filterOptions: {
          enabled: true,
          filterValue: "",
          placeholder: "",
        },
      },
      {
        label: "First",
        field: "first",
        sortable: true,
        hidden: false,
        filterOptions: {
          enabled: true,
          filterValue: "",
          placeholder: "",
        },
      },
      {
        label: "Last",
        field: "last",
        sortable: true,
        hidden: false,
        filterOptions: {
          enabled: true,
          filterValue: "",
          placeholder: "",
        },
      },
      {
        label: "Gander",
        field: "s",
        sortable: true,
        width: "50px",
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "",
        },
      },
      {
        label: "House",
        field: "house",
        sortable: true,
        hidden: false,
        filterOptions: {
          enabled: true,
          filterValue: "",
          placeholder: "",
        },
      },
      {
        label: "Street",
        field: "street",
        sortable: true,
        width: "250px",
        hidden: false,
        filterOptions: {
          enabled: true,
          filterValue: "",
          placeholder: "",
        },
      },
      {
        label: "Apt",
        field: "apt",
        sortable: true,
        width: "50px",
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "",
        },
      },
      {
        label: "City",
        field: "city",
        sortable: true,
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "",
        },
      },
      {
        label: "Zip",
        field: "zip",
        sortable: true,
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "",
        },
      },
      {
        sortable: true,
        label: "Matched",
        field: "isMatched",
        type: "boolean",
        formatFn: (value, row) => {
          return row.isMatched ? "Yes" : "No";
        },
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "Select",
          filterDropdownItems: [
            { value: true, text: "Yes" },
            { value: false, text: "No" },
          ],
        },
      },
      {
        label: "Matched To",
        field: "matchName",
        width: "250px",
        sortable: true,
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "",
        },
      },
      {
        label: "DOB",
        field: "dob",
        sortable: true,
        hidden: false,
        filterOptions: {
          enabled: true,
          placeholder: "",
        },
      },
    ],

    columns: [
      {
        field: "statusIcon",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        allowHiding: false,
        allowSearch: false,
        cssClass: "width-25",
        headerFilter: {
          allowSearch: false,
          groupInterval: undefined,
          height: undefined,
          searchMode: "contains",
        },
        icon: (value) => {
          switch (value) {
            case "SUBMITTED":
              return "mdi-check-circle";
            case "PENDING":
              return "mdi-clock";
            default:
              return "";
          }
        },
        iconColor: (value) => {
          switch (value) {
            case "SUBMITTED":
              return "success";
            case "PENDING":
              return "#fdb400";
            default:
              return "#aeb8bf";
          }
        },
      },
      // {
      //     label: "ID",
      //     field: "id",
      //     visible: true,
      //     filterValues: [],
      //     filterValue: null,
      //     selectedFilterOperation: null,
      // },
      {
        label: "First Name",
        field: "firstName",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Last Name",
        field: "lastName",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "votingStatus",
        label: "Voting Status",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        template: "link-action-cell-template",
        showLink: (row) => {
          return true;
        },
      },
      {
        label: "House Number",
        field: "houseNumber",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Street",
        field: "streetName",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Type",
        field: "type",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Account ID",
        field: "accountID",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Person ID",
        field: "personID",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "date",
        label: "Entered On",
        type: "date",
        format: "",
        visible: true,
        filterType: "",
        format: "MM/dd/yyyy hh:mm:ss a",
        sortOrder: "desc",
        sortIndex: 0,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Form Type",
        field: "formType",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Status",
        field: "status",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Submitted At",
        field: "submittedAt",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Submitted By",
        field: "submittedBy",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Confirmation",
        field: "confirmation",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Note",
        field: "note",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Cell Phone",
        field: "cellPhone",
        format: "",
        visible: false,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Matched To DOE ID",
        field: "matchedToDoeID",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Matched To DOE On",
        field: "matchedToDoeOn",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Matched To DOE By",
        field: "matchedToDoeBy",
        format: "",
        visible: true,
        filterType: "",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),
  async created() {
    this.columns.find((c) => c.field == "votingStatus").linkAction = (row) => {
      this.openVotingDialog(row);
    };

    this.actions.push({
      text: "Show Submitted",
      type: "switch",
      action: this.onShowSubmittedChanged,
    });

    this.actions.push({
      text: "Only Unmatched",
      type: "switch",
      action: this.onOnlyUnmatchedChanged,
    });

    this.actions.push({
      text: "Auto Match DOE",
      action: this.openAutoMatchDialog,
    });
  },
  beforeDestroy() {},
  computed: {},
  methods: {
    async beforeSetupGrid() {
      this.setGridUrl(false);
    },
    onShowSubmittedChanged(value) {
      this.includeSubmitted = !!value;
      this.setGridUrl(true);
    },
    onOnlyUnmatchedChanged(value) {
      this.onlyUnmatched = !!value;
      this.setGridUrl(true);
    },
    setGridUrl(reload = true) {
      const includeSubmitted = this.includeSubmitted ? "true" : "";
      const onlyUnmatched = this.onlyUnmatched ? "true" : "";
      this.url =
        "onlineVotingRegistrationForms?includeSubmitted=" +
        includeSubmitted +
        "&onlyUnmatched=" +
        onlyUnmatched;

      if (reload && this.$refs.grid) {
        this.$refs.grid.reloadDataSource(this.url);
      }
    },
    async onRowClicked(row) {
      this.rowClicked = row;
      this.status = row.status;
      this.originalStatus = row.status;
      this.note = row.note;
      this.confirmation = row.confirmation;
      this.updateDialog = true;
    },
    getProperties(data) {
      const sortBy = [
        "accountID",
        "type",
        "usCitizen",
        "nycResident",
        "firstName",
        "lastName",
        "middleInitial",
        "suffix",
        "dob",
        "gender",
        "phone",
        "emailAddress",
        "dmvNumber",
        "socialLast4",
        "houseNumber",
        "streetName",
        "apartment",
        "borough",
        "cityTownVillage",
        "zipCode",
        "mailingAddressState",
        "mailingAddress",
        "mailingAddressPoBox",
        "mailingAddressZipCode",
        "mailingAddressCityTownVillage",
        "haveYouVotedBefore",
        "votedBeforeYear",
        "nameWas",
        "addressWas",
        "stateOrCountyWas",
        "politicalParty",
      ];

      const dateFields = ["dob", "submittedAt", "matchedToDoeOn"];

      const fields = Object.keys(
        except(
          data,
          "signatureImageUrl",
          "signatureBase64String",
          "status",
          "statusIcon",
          "date",
          "pdfUrl",
          "id",
          "submittedAt",
          "submittedBy",
          "matchedToDoeID",
          "matchedToDoeOn",
          "matchedToDoeBy"
        )
      )
        .map((key) => {
          return {
            originalKey: key,
            name: this.formatField(key),
            value:
              dateFields.includes(key) && data[key]
                ? moment(data[key]).format("L")
                : data[key],
          };
        })
        .filter((p) => p.value)
        .sort((a, b) => {
          // Get the index of each field's original key in the sortBy array
          const indexA = sortBy.indexOf(a.originalKey);
          const indexB = sortBy.indexOf(b.originalKey);

          // If a field is not in sortBy, push it to the end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          // Sort based on index in sortBy
          return indexA - indexB;
        });

      return fields;
    },
    formatField(value) {
      // Step 1: Split camelCase/PascalCase by adding spaces before uppercase letters
      const spacedValue = value
        .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space between lowercase and uppercase (e.g., FirstName -> First Name)
        .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2"); // Handle cases like "DOB" -> "D O B" then adjust

      // Step 2: Convert to title case (capitalize first letter of each word)
      return spacedValue
        .split(" ") // Split into words
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ) // Capitalize each word
        .join(" "); // Join back with spaces
    },
    async openVotingDialog(row) {
      if (row.personID == "No Ansl ID") {
        return this.$notify({
          group: "notification",
          text: "No Ansl ID",
          type: "warning",
        });
      }

      this.presonInfo = await this.$http.get(
        "api/applicantStudents/getPersonByID?personID=" + row.personID
      );

      this.votingDialog = true;
    },
    closeVotingDialog() {
      this.votingDialog = false;
    },
    reload() {
      this.$refs.grid.reloadGrid();
    },
    downloadPdf() {
      this.downloadPDF(this.rowClicked.pdfUrl);
    },
    downloadSignature() {
      window.open(this.rowClicked.signatureImageUrl);
    },
    async downloadPDF(url, fileName = "Pdf Form.pdf") {
      try {
        // Fetch the PDF file
        const response = await fetch(url);
        if (!response.ok) throw new Error("Network response was not ok");

        // Convert the response to a blob
        const blob = await response.blob();

        // Create a temporary URL for the blob
        const blobUrl = window.URL.createObjectURL(blob);

        // Create a temporary link element
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName; // Set the file name for download

        // Append the link to the document, trigger click, and remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Revoke the blob URL to free up memory
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Failed to download PDF:", error);
      }
    },
    parseDoeRegDate(value) {
      if (!value) return "";
      const parsed = moment(String(value), "YYYYMMDD");
      return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
    },
    normalizeNamePrefix(value) {
      if (!value && value !== 0) return "";

      return String(value)
        .toLowerCase()
        .trim()
        .replace(/[^a-z]/g, "")
        .slice(0, 4);
    },
    normalizeHouse(value) {
      if (!value && value !== 0) return "";

      return String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "")
        .trim();
    },
    getLeadingHouseDigits(value) {
      const normalized = this.normalizeHouse(value);
      if (!normalized) return "";

      const match = normalized.match(/^\d+/);
      return match && match[0] ? match[0] : "";
    },
    normalizeStreetFirstWord(value) {
      if (!value && value !== 0) return "";

      const streetSuffixes = [
        "ave",
        "avenue",
        "st",
        "street",
        "rd",
        "road",
        "blvd",
        "boulevard",
        "pl",
        "place",
        "ln",
        "lane",
        "dr",
        "drive",
        "ct",
        "court",
        "ter",
        "terrace",
        "pkwy",
        "parkway",
        "hwy",
        "highway",
      ];

      const cleaned = String(value)
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      if (!cleaned) return "";

      const parts = cleaned.split(" ").filter((part) => part);
      if (!parts.length) return "";

      if (parts.length > 1) {
        const lastPart = parts[parts.length - 1];
        if (streetSuffixes.includes(lastPart)) {
          parts.pop();
        }
      }

      return parts.length ? parts[0] : "";
    },
    escapeODataString(value) {
      if (!value && value !== 0) return "";

      return String(value).replace(/'/g, "''");
    },
    onDoeRowClick(doe) {
      if (this.rowClicked && this.rowClicked.matchedToDoeID) {
        return;
      }

      this.selectDoeRecord(doe);
    },
    async openDoeMatchDialog() {
      this.selectedDoeRecord = null;
      this.doeMatchDialog = true;
      await this.loadDoeRecords();
    },
    closeDoeMatchDialog() {
      this.doeMatchDialog = false;
      this.selectedDoeRecord = null;
      this.doeRecords = [];
      this.formDob = "";
    },
    async loadDoeRecords() {
      try {
        this.doeLoading = true;
        this.doeRecords = [];

        const form = this.rowClicked;
        const last4 = this.normalizeNamePrefix(form.lastName);
        const first4 = this.normalizeNamePrefix(form.firstName);
        const houseNormalized = this.normalizeHouse(form.houseNumber);
        const houseDigits = this.getLeadingHouseDigits(form.houseNumber);
        const streetFirstWord = this.normalizeStreetFirstWord(form.streetName);
        const dob = form.dob ? moment(form.dob).format("YYYY-MM-DD") : "";
        this.formDob = dob;

        let url = "";
        if (form.matchedToDoeID) {
          const mappedId = String(form.matchedToDoeID).trim();
          const mappedFilter = `regID eq '${mappedId}'`;
          url = `odata/doe?isCurrent=true&$filter=${encodeURIComponent(
            mappedFilter
          )}&$top=5`;
        } else {
          const andClauses = [];
          if (last4) {
            andClauses.push(
              `startswith(tolower(last),'${this.escapeODataString(last4)}')`
            );
          }
          if (first4) {
            andClauses.push(
              `startswith(tolower(first),'${this.escapeODataString(first4)}')`
            );
          }
          if (houseDigits) {
            andClauses.push(
              `startswith(house,'${this.escapeODataString(houseDigits)}')`
            );
          }
          // Street suffix differences are handled by client-side normalization too.
          if (streetFirstWord)
            andClauses.push(
              `startswith(tolower(street),'${this.escapeODataString(
                streetFirstWord
              )}')`
            );

          const filterQuery = andClauses.join(" and ");
          url = filterQuery
            ? `odata/doe?isCurrent=true&$filter=${encodeURIComponent(filterQuery)}&$top=50`
            : `odata/doe?isCurrent=true&$top=50`;
        }

        const response = await this.$http.get(url);
        const rawRecords =
          response && response.value
            ? response.value
            : Array.isArray(response)
            ? response
            : [];

        // Client-side refinement keeps behavior aligned with backend automatch logic.
        const records = form.matchedToDoeID
          ? rawRecords
          : rawRecords.filter((d) => {
              const doeFirst = d.first ? String(d.first).toLowerCase() : "";
              const doeLast = d.last ? String(d.last).toLowerCase() : "";
              const doeStreetFirstWord = this.normalizeStreetFirstWord(
                d.street
              );
              const doeHouseNormalized = this.normalizeHouse(d.house);
              const doeDob = d.dob ? String(d.dob).slice(0, 10) : "";

              const nameMatches =
                (!first4 || (doeFirst && doeFirst.startsWith(first4))) &&
                (!last4 || (doeLast && doeLast.startsWith(last4)));

              const streetMatches =
                !streetFirstWord ||
                (doeStreetFirstWord &&
                  doeStreetFirstWord.startsWith(streetFirstWord));

              const houseCompareBase = houseDigits || houseNormalized;
              const houseMatches =
                !houseCompareBase ||
                (doeHouseNormalized &&
                  doeHouseNormalized.startsWith(houseCompareBase));

              return nameMatches && streetMatches && houseMatches;
            });

        this.doeRecords = records;

        // Auto-select if only one result
        if (records.length === 1) {
          this.selectedDoeRecord = records[0];
        }
      } catch (e) {
        this.doeRecords = [];
      } finally {
        this.doeLoading = false;
      }
    },
    selectDoeRecord(doe) {
      if (this.selectedDoeRecord && this.selectedDoeRecord.id === doe.id) {
        this.selectedDoeRecord = null;
      } else {
        this.selectedDoeRecord = doe;
      }
    },
    async confirmDoeMatch() {
      if (!this.selectedDoeRecord) return;

      try {
        this.doeMatchProcessing = true;

        // Keep the footer actions stable while request is in-flight.
        // If we set rowClicked.matchedToDoe* now, the dialog flips to
        // "mapped/remove" mode before save completes.
        const payload = Object.assign({}, this.rowClicked, {
          matchedToDoeID: this.selectedDoeRecord.regID,
          matchedToDoeOn: moment().format("YYYY-MM-DDTHH:mm:ss"),
          matchedToDoeBy: this.$store.state.authentication.user,
        });

        await this.$http.post(
          "api/onlineVotingRegistrationForms/save",
          payload
        );

        // Apply local state only after successful save.
        this.rowClicked.matchedToDoeID = payload.matchedToDoeID;
        this.rowClicked.matchedToDoeOn = payload.matchedToDoeOn;
        this.rowClicked.matchedToDoeBy = payload.matchedToDoeBy;

        this.$notify({
          text: "DOE Match Saved",
          group: "notification",
          type: "success",
        });

        this.doeMatchProcessing = false;
        this.doeMatchDialog = false;
        this.selectedDoeRecord = null;
        this.doeRecords = [];
        this.reload();
      } catch (e) {
        this.doeMatchProcessing = false;
      }
    },
    async removeDoeMatch() {
      try {
        this.doeMatchProcessing = true;

        // Keep current UI state stable while request is in-flight.
        // If we clear rowClicked fields immediately, the footer switches
        // from "Remove" mode to "Confirm" mode before save completes.
        const payload = Object.assign({}, this.rowClicked, {
          matchedToDoeID: null,
          matchedToDoeOn: null,
          matchedToDoeBy: null,
        });

        await this.$http.post(
          "api/onlineVotingRegistrationForms/save",
          payload
        );

        // Apply local state only after successful save.
        this.rowClicked.matchedToDoeID = null;
        this.rowClicked.matchedToDoeOn = null;
        this.rowClicked.matchedToDoeBy = null;

        this.$notify({
          text: "DOE Match Removed",
          group: "notification",
          type: "success",
        });

        this.doeMatchProcessing = false;
        this.doeMatchDialog = false;
        this.selectedDoeRecord = null;
        this.doeRecords = [];
        this.reload();
      } catch (e) {
        this.doeMatchProcessing = false;
      }
    },
    openAutoMatchDialog() {
      this.autoMatchResults = [];
      this.autoMatchIncludeAlreadyMatched = false;
      this.autoMatchDialog = true;
      this.loadAutoMatches();
    },

    closeAutoMatchDialog() {
      this.autoMatchDialog = false;
      this.autoMatchResults = [];
    },

    async loadAutoMatches() {
      try {
        this.autoMatchLoading = true;
        this.autoMatchResults = [];

        const include = this.autoMatchIncludeAlreadyMatched ? "true" : "false";
        const results = await this.$http.get(
          "api/onlineVotingRegistrationForms/GetAutoDoeMatches?includeAlreadyMatched=" +
            include
        );

        this.autoMatchResults = results;
      } catch (e) {
        this.$notify({
          text: "Failed to load auto matches",
          group: "notification",
          type: "error",
        });
      } finally {
        this.autoMatchLoading = false;
      }
    },

    removeAutoMatchItem(index) {
      this.autoMatchResults.splice(index, 1);
    },

    async saveAutoMatches() {
      try {
        this.autoMatchProcessing = true;

        const user = this.$store.state.authentication.user;
        const now = moment().format("YYYY-MM-DDTHH:mm:ss");

        const payload = this.autoMatchResults.map((item) => ({
          id: item.form.id,
          matchedToDoeID: item.doe.reG_ID,
          matchedToDoeOn: now,
          matchedToDoeBy: user,
        }));

        const response = await this.$http.post(
          "api/onlineVotingRegistrationForms/SaveDoeMatches",
          payload
        );

        const updated = response && response.updated ? response.updated : 0;

        this.$notify({
          text:
            updated + " form" + (updated !== 1 ? "s" : "") + " matched to DOE",
          group: "notification",
          type: "success",
        });

        this.autoMatchDialog = false;
        this.autoMatchResults = [];
        this.reload();
      } catch (e) {
        this.$notify({
          text: "Failed to save DOE matches",
          group: "notification",
          type: "error",
        });
      } finally {
        this.autoMatchProcessing = false;
      }
    },

    openManualDoeMatchDialog() {
      this.manualClickedDoe = {};
      this.manualShowDoeGrid = true;
      this.manualDoeMatchDialog = true;
    },
    closeManualDoeMatchDialog() {
      this.manualDoeMatchDialog = false;
      this.manualShowDoeGrid = false;
      this.manualClickedDoe = {};

      const col = this.manualDoeColumns.find((c) => c.field == "first");
      if (col) col.filterOptions.filterValue = "";
      const lastCol = this.manualDoeColumns.find((c) => c.field == "last");
      if (lastCol) lastCol.filterOptions.filterValue = "";
      const houseCol = this.manualDoeColumns.find((c) => c.field == "house");
      if (houseCol) houseCol.filterOptions.filterValue = "";
      const streetCol = this.manualDoeColumns.find((c) => c.field == "street");
      if (streetCol) streetCol.filterOptions.filterValue = "";
    },
    beforeSetupManualDoeGrid() {
      const form = this.rowClicked;

      const lastCol = this.manualDoeColumns.find((c) => c.field == "last");
      if (lastCol && form.lastName) {
        lastCol.filterOptions.filterValue = form.lastName.slice(0, 4);
      }

      const houseNumber = form.houseNumber ? String(form.houseNumber).split(" ")[0] : "";
      const street = form.streetName ? String(form.streetName).split(" ")[0] : "";

      const houseCol = this.manualDoeColumns.find((c) => c.field == "house");
      if (houseCol) houseCol.filterOptions.filterValue = houseNumber ? houseNumber.slice(0, 4) : "";

      const streetCol = this.manualDoeColumns.find((c) => c.field == "street");
      if (streetCol) streetCol.filterOptions.filterValue = street ? street.slice(0, 4) : "";

      this.manualDoeUrl = "doe?isCurrent=true";
    },
    onManualDoeRowClicked(row) {
      this.manualClickedDoe = row;
      this.manualClickedDoe.matchType =
        this.manualClickedDoe.s == "M"
          ? "Husband"
          : this.manualClickedDoe.s == "F"
          ? "Wife"
          : "";
    },
    selectManualMatch() {
      if (!this.manualClickedDoe || !this.manualClickedDoe.first) return;

      const doe = this.manualClickedDoe;
      this.doeRecords = [doe];
      this.selectedDoeRecord = doe;
      this.manualDoeMatchDialog = false;
      this.manualShowDoeGrid = false;
      this.manualClickedDoe = {};
    },
    async save() {
      try {
        this.processing = true;

        this.rowClicked.status = this.status;
        this.rowClicked.note = this.note;
        this.rowClicked.confirmation = this.confirmation;

        if (this.status === "SUBMITTED") {
          const isNewSubmission =
            this.originalStatus !== "SUBMITTED" ||
            !this.rowClicked.submittedAt ||
            !this.rowClicked.submittedBy;

          if (isNewSubmission) {
            this.rowClicked.submittedAt = new Date().toISOString();
            this.rowClicked.submittedBy = this.$store.state.authentication.user;
          }
        }

        await this.$http.post(
          "api/onlineVotingRegistrationForms/save",
          this.rowClicked
        );

        this.processing = false;
        this.updateDialog = false;

        this.$notify({
          text: "Changes Saved",
          group: "notification",
          type: "success",
        });

        this.reload();
      } catch (e) {
        this.processing = false;
      }
    },
  },
};
</script>
<style>
[v-cloak] {
  display: none;
}
.v-btn__content {
  color: unset;
}
.accounts-search.container {
  padding-top: 0px;
}
.yg-student-account-match {
  border: 2px solid #4caf50;
}
.doe-dob-mismatch {
    color: #f57c00;
    font-weight: 500;
}
.doe-dob-mismatch-note {
    color: #f57c00;
    font-size: 11px;
    margin-top: 1px;
}
.automatch-tag--dob-mismatch {
    background-color: #fff3e0 !important;
    color: #e65100 !important;
    border: 1px solid #ffcc80 !important;
    font-weight: 500;
}
.doe-mapping-banner {
  border-radius: 6px;
}
.doe-mapping-banner--matched {
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
}
.doe-mapping-banner--unmatched {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
}
.doe-map-btn-mapped {
  background: #ffffff !important;
  border: 1px solid #66bb6a !important;
  color: #2e7d32 !important;
}
.doe-form-banner {
  background: #f5f5f5;
  border-left: 4px solid #4caf50;
  border-radius: 4px;
}
.doe-candidate-row {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.doe-candidate-row:hover {
  background: #fafafa;
  border-color: #bdbdbd;
}
.doe-candidate-selected {
  border: 2px solid #4caf50 !important;
  background: #e8f5e9 !important;
}
.doe-dialog-actions {
  width: 100%;
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.automatch-header {
  background: #4caf50;
}
.automatch-col-headers {
  background: #f5f5f5;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}
.automatch-footer {
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}
.automatch-row {
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  background: #fff;
  border: 1px solid #e8e8e8;
  transition: box-shadow 0.15s;
}
.automatch-row:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: #c8e6c9;
}
.automatch-side {
  border-radius: 0;
}
.automatch-side--form {
  border-right: 1px solid #f0f0f0;
}
.automatch-tag {
  display: inline-block;
  font-size: 11px;
  background: #f3f3f3;
  border-radius: 3px;
  padding: 1px 6px;
  margin-right: 4px;
  margin-top: 2px;
  color: #555;
}
.automatch-tag--id {
  background: #e8f5e9;
  color: #2e7d32;
  font-weight: 600;
}
</style>
